"use client";
import React, { ReactNode } from "react";
import { Modal } from "./index";
import Typography from "@/components/ui/Typography";

interface ScrollableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  maxHeight?: string;
  titleSize?: "sm" | "md" | "lg" | "xl";
  contentClassName?: string;
}

/**
 * ScrollableModal - An enhanced modal with sticky header, scrollable content, and sticky footer
 * 
 * @param isOpen - Controls whether the modal is visible
 * @param onClose - Function called when the modal should close
 * @param title - Title text or custom component for the header
 * @param children - Content to display in the scrollable area
 * @param footer - Optional footer content (typically action buttons)
 * @param className - Optional additional classes for the modal container
 * @param maxHeight - Optional max height (default: 90vh)
 * @param titleSize - Size of the title: sm, md, lg, xl (default: lg)
 * @param contentClassName - Optional classes for the scrollable content area (default: p-6 pt-4)
 */
const ScrollableModal: React.FC<ScrollableModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  className = "max-w-md",
  maxHeight = "90vh",
  titleSize = "lg",
  contentClassName = "p-6 pt-4"
}) => {
  // Map title size to appropriate text classes
  const getTitleClasses = () => {
    switch(titleSize) {
      case "sm": 
        return "text-lg font-medium";
      case "md": 
        return "text-xl font-semibold";
      case "lg": 
        return "text-2xl font-semibold";
      case "xl": 
        return "text-3xl font-semibold";
      default: 
        return "text-2xl font-semibold";
    }
  };

  // Get Typography variant based on title size
  const getTypographyVariant = () => {
    switch(titleSize) {
      case "sm": return "h5";
      case "md": return "h4";
      case "lg": return "h3";
      case "xl": return "h2";
      default: return "h3";
    }
  };

  // Calculate content area max height, accounting for header and footer
  const getContentMaxHeight = () => {
    const footerHeight = footer ? "150px" : "120px"; // Adjust if footer is present
    return `calc(${maxHeight} - ${footerHeight})`;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      className={`${className} rounded-3xl overflow-hidden`}
    >
      <div className="flex flex-col" style={{ maxHeight }}>
        {/* Header with padding that matches the close button positioning */}
        <div className="pt-6 pb-4 px-6 border-b border-gray-100 dark:border-gray-700">
          {/* Fixed-height container to align with close button */}
          <div className="h-11 flex items-center">
            {typeof title === 'string' ? (
              <Typography variant={getTypographyVariant()} className={getTitleClasses()}>
                {title}
              </Typography>
            ) : (
              title
            )}
          </div>
        </div>
        
        {/* Scrollable content area */}
        <div className={`modal-scrollable-content overflow-y-auto ${contentClassName}`} style={{ maxHeight: getContentMaxHeight() }}>
          {children}
        </div>
        
        {/* Footer - only rendered if provided */}
        {footer && (
          <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center">
            <div className="w-full">
              {footer}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ScrollableModal; 